import type { PreferenceStoreIdentifier } from '@transcend-io/privacy-types';

import type { ConsentPreferenceUpdateInput } from '@main/cm-types';
import { convertPSIdentifiersToEnrichedIdentifiers } from '@main/cm-types';
import type { EnrichedIdentifiers, PurposeInput } from '@main/sombra-types';

import type { Purpose } from '../CommunicationsPreferences/components';

/**
 * Create input for updateDataSubjectConsentPreferences mutation
 *
 * @param coreIdentifier - the core identifier of the data subject
 * @param identifiers - set of identifiers on consent record
 * @param partition - the partition to update
 * @param purposes - the selected purposes/preferences
 * @returns input for updateDataSubjectConsentPreferences mutation
 */
export function getUpdateDataSubjectConsentPreferencesInput(
  coreIdentifier: string,
  identifiers: PreferenceStoreIdentifier[],
  partition: string,
  purposes: Record<string, Purpose>,
): {
  /** Update Consent Preferences input */
  input: ConsentPreferenceUpdateInput;
  /** Diffie Hellman input */
  dhEncryptedInput: {
    /** Identifiers to encrypt */
    identifiersToEncrypt: PreferenceStoreIdentifier[][];
    /** Attested auth context for triggers dsr's  */
    attestedExtraIdentifiers: EnrichedIdentifiers | undefined;
  };
} {
  const identifiersToEncrypt =
    identifiers.length > 0
      ? [identifiers]
      : [[{ name: 'email', value: coreIdentifier }]];
  return {
    input: {
      partition,
      purposes: Object.entries(purposes).map(
        ([purpose, { consent, preferences }]) => ({
          purpose,
          consent,
          ...(preferences
            ? {
                preferences: Object.entries(preferences).map(
                  ([topic, choice]) => {
                    // remove __typename from choice before calling graphql
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const { __typename, ...rest } = choice as any;
                    return { topic, choice: { ...rest } };
                  },
                ),
              }
            : {}),
        }),
      ) as PurposeInput[],
    },
    dhEncryptedInput: {
      identifiersToEncrypt,
      attestedExtraIdentifiers: convertPSIdentifiersToEnrichedIdentifiers(
        identifiers,
        [coreIdentifier],
      ),
    },
  };
}
